import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, FileText, TrendingUp, Star } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";

const CARDS = [
  ["total_views", "Total Views", Eye, "text-cyan"],
  ["published", "Published Articles", FileText, "text-emerald"],
  ["avg_views", "Avg Views / Article", TrendingUp, "text-amber"],
  ["editors_picks", "Editors' Picks", Star, "text-crimson"],
];

const fmt = (n) => (n ?? 0).toLocaleString("en-US");

function Bars({ rows, labelKey }) {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r[labelKey]} data-testid={`analytics-bar-${r[labelKey]}`}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-admtext capitalize">{(r[labelKey] || "").replace("-", " ")}</span>
            <span className="text-admtext2 font-mono">{fmt(r.views)} views · {r.count}</span>
          </div>
          <div className="h-2 bg-admbg border border-admborder overflow-hidden">
            <div className="h-full bg-cyan/70" style={{ width: `${Math.round((r.views / max) * 100)}%` }} />
          </div>
        </div>
      ))}
      {!rows.length && <div className="text-admmuted text-sm">No published articles yet.</div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);

  const s = data?.summary || {};
  const top = data?.top_articles || [];

  return (
    <AdminLayout title="Analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {CARDS.map(([key, label, Icon, color]) => (
          <div key={key} className="bg-admcard border border-admborder p-4" data-testid={`analytics-stat-${key}`}>
            <div className="flex items-center justify-between">
              <div className={`font-mono text-3xl font-medium ${color}`}>{data ? fmt(s[key]) : "—"}</div>
              <Icon size={18} className={color} />
            </div>
            <div className="text-xs text-admtext2 uppercase tracking-wider mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-admcard border border-admborder">
        <div className="px-5 py-3 border-b border-admborder">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-admtext2">Most Read Articles</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-admmuted border-b border-admborder">
              <th className="px-5 py-2 w-8">#</th>
              <th className="px-3 py-2">Headline</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 text-right">Views</th>
            </tr>
          </thead>
          <tbody data-testid="analytics-top-articles">
            {top.map((a, i) => (
              <tr key={a.id} className="border-b border-admborder last:border-0 hover:bg-admcardh transition-colors">
                <td className="px-5 py-2.5 font-mono text-admmuted">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <Link to={`/admin/articles/${a.id}`} className="text-admtext hover:text-cyan flex items-center gap-1.5">
                    {a.editors_pick && <Star size={11} className="text-crimson shrink-0" />}
                    <span className="truncate">{a.headline}</span>
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-admtext2 capitalize">{(a.category || "").replace("-", " ")}</td>
                <td className="px-3 py-2.5 text-right font-mono text-cyan">{fmt(a.views)}</td>
              </tr>
            ))}
            {!top.length && <tr><td colSpan={4} className="px-5 py-6 text-admmuted text-sm">No published articles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-admcard border border-admborder p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-admtext2 mb-4">Views by Category</h2>
          <Bars rows={data?.by_category || []} labelKey="category" />
        </div>
        <div className="bg-admcard border border-admborder p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-admtext2 mb-4">Views by Content Type</h2>
          <Bars rows={data?.by_type || []} labelKey="content_type" />
        </div>
      </div>
    </AdminLayout>
  );
}
